import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Visibility as ViewIcon,
  Reorder as ReorderIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { getBoardMembers, createOrUpdateBoardMember, deleteBoardMember, uploadBoardMemberImage, BoardMember } from '../utils/boardMembersApi';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001';

const BoardMembersManagement: React.FC = () => {
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<BoardMember | null>(null);
  const [uploadingImage, setUploadingImage] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    image: '',
    bio: '',
    order_index: 0,
    is_active: true
  });

  // Load board members from database
  useEffect(() => {
    let isMounted = true;
    
    const loadBoardMembers = async () => {
      try {
        setLoading(true);
        const data = await getBoardMembers();
        if (isMounted) {
          setBoardMembers(data);
        }
      } catch (err) {
        console.error('Error loading board members:', err);
        if (isMounted) {
          setError('Failed to load board members');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBoardMembers();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleOpenDialog = (member?: BoardMember) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        position: member.position,
        image: member.image,
        bio: member.bio,
        order_index: member.order_index,
        is_active: member.is_active
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        position: '',
        image: '',
        bio: '',
        order_index: boardMembers.length,
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMember(null);
    setFormData({
      name: '',
      position: '',
      image: '',
      bio: '',
      order_index: 0,
      is_active: true
    });
  };

  const handleSaveMember = async () => {
    try {
      const memberData = {
        ...formData,
        id: editingMember?.id
      };
      
      const savedMember = await createOrUpdateBoardMember(memberData);
      
      if (editingMember) {
        setBoardMembers(prev => 
          prev.map(member => member.id === editingMember.id ? savedMember : member)
        );
      } else {
        setBoardMembers(prev => [...prev, savedMember]);
      }
      
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving board member:', err);
      setError('Failed to save board member');
    }
  };

  const handleDeleteMember = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this board member?')) {
      try {
        await deleteBoardMember(id);
        setBoardMembers(prev => prev.filter(member => member.id !== id));
      } catch (err) {
        console.error('Error deleting board member:', err);
        setError('Failed to delete board member');
      }
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (memberId: number, file: File) => {
    try {
      setUploadingImage(memberId);
      const result = await uploadBoardMemberImage(memberId, file);
      
      // Update the member's image URL in the local state
      setBoardMembers(prev => 
        prev.map(member => 
          member.id === memberId 
            ? { ...member, image: result.imageUrl }
            : member
        )
      );
      if (editingMember?.id === memberId) {
        setEditingMember(prev => (prev ? { ...prev, image: result.imageUrl } : prev));
        setFormData(prev => ({ ...prev, image: result.imageUrl }));
      }
      
      setSuccess('Image uploaded successfully');
    } catch (err) {
      setError('Failed to upload image');
      console.error(err);
    } finally {
      setUploadingImage(null);
    }
  };

  const resolveImageUrl = (imagePath: string | null | undefined) => {
    if (!imagePath) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRTVFN0VCIi8+CjxwYXRoIGQ9Ik0yMDAgMTIwQzE3OC43ODkgMTIwIDE2MC44MTEgMTM4IDI4LDE2MC44MTEgMTYwLjgxMSAyMjAuODExIDI0MCAxNjAuODExIDI0MCAxMjBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMjAgMjgwQzEyMCAyMzUuODg1IDE1NS44ODUgMjAwIDIwMCAyMDBDMjQ0LjExNSAyMDAgMjgwIDIzNS44ODUgMjgwIDI4MFYzMjBIMTIwVjI4MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
    }
    if (imagePath.startsWith(`${API_BASE}/api/board-members/`)) {
      return imagePath; // Already a direct API image URL
    }
    if (imagePath.startsWith('http')) {
      return imagePath; // External URL
    }
    if (imagePath.startsWith('/images/')) {
      return `${API_BASE}${imagePath}`; // Old path-based image
    }
    return imagePath; // Fallback for other cases
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Board Members Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Board Member
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {boardMembers.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.id}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    src={resolveImageUrl(member.image)}
                    alt={member.name}
                    sx={{ width: 60, height: 60, mr: 2, bgcolor: '#f0f0f0' }}
                    imgProps={{ style: { objectFit: 'contain' } }}
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRTVFN0VCIi8+CjxwYXRoIGQ9Ik0yMDAgMTIwQzE3OC43ODkgMTIwIDE2MC44MTEgMTM4IDI4LDE2MC44MTEgMTYwLjgxMSAyMjAuODExIDI0MCAxNjAuODExIDI0MCAxMjBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMjAgMjgwQzEyMCAyMzUuODg1IDE1NS44ODUgMjAwIDIwMCAyMDBDMjQ0LjExNSAyMDAgMjgwIDIzNS44ODUgMjgwIDI4MFYzMjBIMTIwVjI4MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="h3">
                      {member.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.position}
                    </Typography>
                    <Chip
                      label={member.is_active ? 'Active' : 'Inactive'}
                      color={member.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {member.bio.length > 100 ? `${member.bio.substring(0, 100)}...` : member.bio}
                </Typography>

                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && member.id) {
                        handleImageUpload(member.id, file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id={`upload-${member.id}`}
                  />
                  <label htmlFor={`upload-${member.id}`}>
                    <Button
                      variant="outlined"
                      size="small"
                      component="span"
                      disabled={uploadingImage === member.id}
                      startIcon={uploadingImage === member.id ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                    >
                      {uploadingImage === member.id ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  </label>
                </Box>
              </CardContent>
              
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenDialog(member)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => member.id && handleDeleteMember(member.id)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMember ? 'Edit Board Member' : 'Add Board Member'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box
                sx={{
                  width: '100%',
                  height: 280,
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                  backgroundImage: `url(${resolveImageUrl(formData.image)})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                value={formData.position}
                onChange={(e) => handleInputChange('position', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                value={formData.image}
                onChange={(e) => handleInputChange('image', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && editingMember?.id) {
                      handleImageUpload(editingMember.id, file);
                    }
                  }}
                  style={{ display: 'none' }}
                  id="upload-edit-member"
                />
                <label htmlFor="upload-edit-member">
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={!editingMember?.id || uploadingImage === editingMember?.id}
                    startIcon={uploadingImage === editingMember?.id ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                  >
                    {uploadingImage === editingMember?.id ? 'Uploading...' : 'Upload Image'}
                  </Button>
                </label>
                {!editingMember?.id && (
                  <Typography variant="caption" color="text.secondary">
                    Save first to enable upload
                  </Typography>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Order Index"
                type="number"
                value={formData.order_index}
                onChange={(e) => handleInputChange('order_index', parseInt(e.target.value) || 0)}
              />
            </Grid>
            
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                multiline
                rows={4}
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Brief biography of the board member..."
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveMember} variant="contained">
            {editingMember ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoardMembersManagement;
